/**
 * Asset Cache Error Types
 *
 * Provides typed error handling for all cache operations.
 * Each error type corresponds to a specific failure mode in the caching system.
 */

/**
 * Error codes for cache operations
 */
export enum CacheErrorCode {
  // Initialization errors
  CACHE_API_NOT_SUPPORTED = "CACHE_API_NOT_SUPPORTED",
  CACHE_NOT_INITIALIZED = "CACHE_NOT_INITIALIZED",
  CACHE_OPEN_FAILED = "CACHE_OPEN_FAILED",

  // Manifest errors
  MANIFEST_NOT_FOUND = "MANIFEST_NOT_FOUND",
  MANIFEST_PARSE_ERROR = "MANIFEST_PARSE_ERROR",
  MANIFEST_SAVE_FAILED = "MANIFEST_SAVE_FAILED",

  // Asset errors
  ASSET_NOT_CACHED = "ASSET_NOT_CACHED",
  ASSET_EXPIRED = "ASSET_EXPIRED",
  ASSET_FETCH_FAILED = "ASSET_FETCH_FAILED",
  ASSET_TOO_LARGE = "ASSET_TOO_LARGE",
  ASSET_INVALID_RESPONSE = "ASSET_INVALID_RESPONSE",

  // Service Worker errors
  SERVICE_WORKER_NOT_SUPPORTED = "SERVICE_WORKER_NOT_SUPPORTED",
  SERVICE_WORKER_REGISTRATION_FAILED = "SERVICE_WORKER_REGISTRATION_FAILED",
  SERVICE_WORKER_COMMUNICATION_FAILED = "SERVICE_WORKER_COMMUNICATION_FAILED",

  // Storage errors
  STORAGE_QUOTA_EXCEEDED = "STORAGE_QUOTA_EXCEEDED",
  STORAGE_ACCESS_DENIED = "STORAGE_ACCESS_DENIED",

  // Generic errors
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Base class for all cache-related errors
 */
export class CacheError extends Error {
  public readonly code: CacheErrorCode;
  public readonly recoverable: boolean;
  public readonly details?: Record<string, unknown>;
  public readonly originalCause?: Error;

  constructor(
    code: CacheErrorCode,
    message: string,
    options?: {
      recoverable?: boolean;
      details?: Record<string, unknown>;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = "CacheError";
    this.code = code;
    this.recoverable = options?.recoverable ?? false;
    this.details = options?.details;
    this.originalCause = options?.cause;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CacheError);
    }
  }

  /**
   * Convert error to JSON for logging/serialization
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      recoverable: this.recoverable,
      details: this.details,
      stack: this.stack,
    };
  }
}

// ============================================================================
// INITIALIZATION ERRORS
// ============================================================================

/**
 * Error thrown when Cache API is not supported in the browser
 */
export class CacheAPINotSupportedError extends CacheError {
  constructor() {
    super(
      CacheErrorCode.CACHE_API_NOT_SUPPORTED,
      "Cache API is not supported in this browser. Asset caching will be disabled.",
      { recoverable: false }
    );
    this.name = "CacheAPINotSupportedError";
  }
}

/**
 * Error thrown when cache operations are attempted before initialization
 */
export class CacheNotInitializedError extends CacheError {
  constructor(operation?: string) {
    super(
      CacheErrorCode.CACHE_NOT_INITIALIZED,
      `Cache is not initialized${operation ? `. Cannot perform: ${operation}` : ""}. Call init() first.`,
      { recoverable: true, details: { operation } }
    );
    this.name = "CacheNotInitializedError";
  }
}

/**
 * Error thrown when opening the cache fails
 */
export class CacheOpenFailedError extends CacheError {
  constructor(cacheName: string, cause?: Error) {
    super(
      CacheErrorCode.CACHE_OPEN_FAILED,
      `Failed to open cache: ${cacheName}`,
      { recoverable: true, details: { cacheName }, cause }
    );
    this.name = "CacheOpenFailedError";
  }
}

// ============================================================================
// MANIFEST ERRORS
// ============================================================================

/**
 * Error thrown when manifest is not found in cache
 */
export class ManifestNotFoundError extends CacheError {
  constructor() {
    super(
      CacheErrorCode.MANIFEST_NOT_FOUND,
      "Cache manifest not found. A new manifest will be created.",
      { recoverable: true }
    );
    this.name = "ManifestNotFoundError";
  }
}

/**
 * Error thrown when manifest JSON parsing fails
 */
export class ManifestParseError extends CacheError {
  constructor(cause?: Error) {
    super(
      CacheErrorCode.MANIFEST_PARSE_ERROR,
      "Failed to parse cache manifest. A new manifest will be created.",
      { recoverable: true, cause }
    );
    this.name = "ManifestParseError";
  }
}

/**
 * Error thrown when saving manifest fails
 */
export class ManifestSaveFailedError extends CacheError {
  constructor(cause?: Error) {
    super(
      CacheErrorCode.MANIFEST_SAVE_FAILED,
      "Failed to save cache manifest. Changes may not persist.",
      { recoverable: true, cause }
    );
    this.name = "ManifestSaveFailedError";
  }
}

// ============================================================================
// ASSET ERRORS
// ============================================================================

/**
 * Error thrown when requested asset is not in cache
 */
export class AssetNotCachedError extends CacheError {
  constructor(url: string) {
    super(
      CacheErrorCode.ASSET_NOT_CACHED,
      `Asset not found in cache: ${url}`,
      { recoverable: true, details: { url } }
    );
    this.name = "AssetNotCachedError";
  }
}

/**
 * Error thrown when cached asset has expired
 */
export class AssetExpiredError extends CacheError {
  constructor(url: string, expiredAt: Date) {
    super(CacheErrorCode.ASSET_EXPIRED, `Cached asset has expired: ${url}`, {
      recoverable: true,
      details: { url, expiredAt: expiredAt.toISOString() },
    });
    this.name = "AssetExpiredError";
  }
}

/**
 * Error thrown when fetching asset from network fails
 */
export class AssetFetchFailedError extends CacheError {
  constructor(url: string, status?: number, statusText?: string, cause?: Error) {
    super(
      CacheErrorCode.ASSET_FETCH_FAILED,
      `Failed to fetch asset: ${url}${status ? ` (${status} ${statusText || ""})` : ""}`,
      { recoverable: true, details: { url, status, statusText }, cause }
    );
    this.name = "AssetFetchFailedError";
  }
}

/**
 * Error thrown when asset exceeds maximum cacheable size
 */
export class AssetTooLargeError extends CacheError {
  constructor(url: string, size: number, maxSize: number) {
    super(
      CacheErrorCode.ASSET_TOO_LARGE,
      `Asset too large to cache: ${url} (${formatBytes(size)} > ${formatBytes(maxSize)})`,
      { recoverable: false, details: { url, size, maxSize } }
    );
    this.name = "AssetTooLargeError";
  }
}

/**
 * Error thrown when response is invalid (status >= 400)
 */
export class AssetInvalidResponseError extends CacheError {
  constructor(url: string, status: number, statusText: string) {
    super(
      CacheErrorCode.ASSET_INVALID_RESPONSE,
      `Invalid response for asset: ${url} (${status} ${statusText})`,
      { recoverable: true, details: { url, status, statusText } }
    );
    this.name = "AssetInvalidResponseError";
  }
}

// ============================================================================
// SERVICE WORKER ERRORS
// ============================================================================

/**
 * Error thrown when Service Worker API is not supported
 */
export class ServiceWorkerNotSupportedError extends CacheError {
  constructor() {
    super(
      CacheErrorCode.SERVICE_WORKER_NOT_SUPPORTED,
      "Service Worker is not supported in this browser.",
      { recoverable: false }
    );
    this.name = "ServiceWorkerNotSupportedError";
  }
}

/**
 * Error thrown when Service Worker registration fails
 */
export class ServiceWorkerRegistrationFailedError extends CacheError {
  constructor(path: string, cause?: Error) {
    super(
      CacheErrorCode.SERVICE_WORKER_REGISTRATION_FAILED,
      `Failed to register service worker: ${path}`,
      { recoverable: true, details: { path }, cause }
    );
    this.name = "ServiceWorkerRegistrationFailedError";
  }
}

/**
 * Error thrown when communication with Service Worker fails
 */
export class ServiceWorkerCommunicationFailedError extends CacheError {
  constructor(messageType: string, cause?: Error) {
    super(
      CacheErrorCode.SERVICE_WORKER_COMMUNICATION_FAILED,
      `Failed to communicate with service worker: ${messageType}`,
      { recoverable: true, details: { messageType }, cause }
    );
    this.name = "ServiceWorkerCommunicationFailedError";
  }
}

// ============================================================================
// STORAGE ERRORS
// ============================================================================

/**
 * Error thrown when storage quota is exceeded
 */
export class StorageQuotaExceededError extends CacheError {
  constructor(cause?: Error) {
    super(
      CacheErrorCode.STORAGE_QUOTA_EXCEEDED,
      "Storage quota exceeded. Consider clearing old cache entries.",
      { recoverable: true, cause }
    );
    this.name = "StorageQuotaExceededError";
  }
}

/**
 * Error thrown when storage access is denied
 */
export class StorageAccessDeniedError extends CacheError {
  constructor(cause?: Error) {
    super(
      CacheErrorCode.STORAGE_ACCESS_DENIED,
      "Storage access denied. This may happen in private/incognito mode.",
      { recoverable: false, cause }
    );
    this.name = "StorageAccessDeniedError";
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Check if an error is a storage quota exceeded error
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (error instanceof DOMException) {
    // Different browsers use different names
    return (
      error.name === "QuotaExceededError" ||
      error.name === "NS_ERROR_DOM_QUOTA_REACHED"
    );
  }
  return false;
}

/**
 * Check if an error is a storage access denied error
 */
export function isStorageAccessDeniedError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return (
      error.name === "SecurityError" ||
      error.name === "InvalidAccessError" ||
      error.name === "NotAllowedError"
    );
  }
  return false;
}

/**
 * Wrap an unknown error in a CacheError
 */
export function wrapError(error: unknown, context?: string): CacheError {
  if (error instanceof CacheError) {
    return error;
  }

  if (isQuotaExceededError(error)) {
    return new StorageQuotaExceededError(error as Error);
  }

  if (isStorageAccessDeniedError(error)) {
    return new StorageAccessDeniedError(error as Error);
  }

  const message = error instanceof Error ? error.message : String(error);
  return new CacheError(
    CacheErrorCode.UNKNOWN_ERROR,
    context ? `${context}: ${message}` : message,
    {
      recoverable: true,
      cause: error instanceof Error ? error : undefined,
    }
  );
}
